/**
 * Modal that polls and renders progress for a single bulk job. Designed to
 * be opened immediately after `submitJob({...})` resolves. Closing the
 * modal does NOT cancel the job — it keeps running on the server. Use the
 * explicit "Cancel job" button to actually request cancellation.
 *
 * Accessibility: Bootstrap `modal` markup with explicit `role="dialog"`,
 * `aria-modal="true"`, `aria-labelledby`. Progress bar has `role="progressbar"`
 * with `aria-valuenow` / `aria-valuemin` / `aria-valuemax` so screen readers
 * announce updates as the count climbs.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  cancelJob,
  isTerminalStatus,
  parseFailureSamples,
  useJobPolling,
  type BulkJob,
  type JobStatus
} from '../lib/jobs';

interface Props {
  jobPk: string | undefined;
  jobId: string | undefined;
  /** Called when the user dismisses the modal. Does NOT cancel the job. */
  onClose: () => void;
  /** Optional: invoked the first time the job reaches a terminal state. */
  onComplete?: (job: BulkJob) => void;
}

export default function BulkJobModal({ jobPk, jobId, onClose, onComplete }: Props) {
  const open = !!jobPk && !!jobId;
  const { job, error } = useJobPolling(jobPk, jobId, { enabled: open });
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | undefined>(undefined);

  // Fire onComplete exactly once per (jobPk, jobId) when the job reaches a
  // terminal status. Use a ref keyed by `${pk}|${id}` so a follow-up job
  // submitted into the same modal instance gets its own one-shot.
  const completedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !job) return;
    if (!isTerminalStatus(job.status)) return;
    const key = `${jobPk}|${jobId}`;
    if (completedKeyRef.current === key) return;
    completedKeyRef.current = key;
    onComplete?.(job);
  }, [open, job, jobPk, jobId, onComplete]);

  // Reset transient cancel UI state when a new job is opened.
  useEffect(() => {
    setCancelling(false);
    setCancelError(undefined);
  }, [jobPk, jobId]);

  const failures = useMemo(() => parseFailureSamples(job), [job]);
  if (!open) return null;

  const pct = job && job.total > 0 ? Math.min(100, Math.round((job.done / job.total) * 100)) : 0;
  const terminal = !!job && isTerminalStatus(job.status);

  const onCancel = async () => {
    if (!jobPk || !jobId) return;
    setCancelling(true);
    setCancelError(undefined);
    try {
      await cancelJob(jobPk, jobId);
    } catch (e) {
      setCancelError(e instanceof Error ? e.message : String(e));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop fade show" />
      <div
        className="modal fade show d-block"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-job-title"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title h5" id="bulk-job-title">
                {job?.label ?? 'Bulk job'}
              </h2>
              <button
                type="button"
                className="btn-close"
                aria-label="Close (job continues in background)"
                onClick={onClose}
              />
            </div>
            <div className="modal-body">
              {!job && !error && <p className="text-muted mb-0">Loading job…</p>}
              {error && (
                <div className="alert alert-warning" role="alert">
                  Could not load job status: {error.message}
                </div>
              )}
              {job && (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-2 small text-muted">
                    <StatusBadge status={job.status} />
                    <span>
                      {job.done} / {job.total}
                      {job.failed > 0 ? <> &middot; <span className="text-danger">{job.failed} failed</span></> : null}
                    </span>
                  </div>
                  <div className="progress" style={{ height: '0.75rem' }} aria-label="Job progress">
                    <div
                      className={`progress-bar ${progressBarClass(job.status, job.failed)}`}
                      role="progressbar"
                      style={{ width: `${pct}%` }}
                      aria-valuenow={pct}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      {pct}%
                    </div>
                  </div>

                  {failures.length > 0 && (
                    <details className="mt-3">
                      <summary className="small">
                        Recent failures ({failures.length}
                        {job.failed > failures.length ? ` of ${job.failed}` : ''})
                      </summary>
                      <ul className="list-unstyled small mt-2 mb-0" style={{ maxHeight: 240, overflowY: 'auto' }}>
                        {failures
                          .slice()
                          .reverse()
                          .map((f, i) => (
                            <li key={`${f.at}-${i}`} className="border-bottom py-1">
                              <strong>{f.name}:</strong> {f.error}
                            </li>
                          ))}
                      </ul>
                    </details>
                  )}

                  {cancelError && (
                    <div className="alert alert-warning mt-3 mb-0" role="alert">
                      Cancel failed: {cancelError}
                    </div>
                  )}

                  <p className="small text-muted mt-3 mb-0">
                    {terminal
                      ? 'Job finished. You can close this dialog.'
                      : 'Closing this dialog leaves the job running. Use Cancel job to stop it.'}
                  </p>
                </>
              )}
            </div>
            <div className="modal-footer">
              {!terminal && (
                <button
                  type="button"
                  className="btn btn-outline-danger me-auto"
                  onClick={onCancel}
                  disabled={cancelling || !job}
                >
                  {cancelling ? 'Cancelling…' : 'Cancel job'}
                </button>
              )}
              <button type="button" className="btn btn-primary" onClick={onClose}>
                {terminal ? 'Close' : 'Close (keep running)'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function StatusBadge({ status }: { status: JobStatus }) {
  const map: Record<JobStatus, { label: string; cls: string }> = {
    queued: { label: 'Queued', cls: 'bg-secondary' },
    running: { label: 'Running', cls: 'bg-primary' },
    completed: { label: 'Completed', cls: 'bg-success' },
    'completed-with-errors': { label: 'Completed (errors)', cls: 'bg-warning text-dark' },
    cancelled: { label: 'Cancelled', cls: 'bg-secondary' },
    failed: { label: 'Failed', cls: 'bg-danger' }
  };
  const { label, cls } = map[status];
  return <span className={`badge ${cls}`}>{label}</span>;
}

function progressBarClass(status: JobStatus, failed: number): string {
  if (status === 'failed') return 'bg-danger';
  if (status === 'cancelled') return 'bg-secondary';
  if (status === 'completed') return 'bg-success';
  if (status === 'completed-with-errors') return 'bg-warning';
  if (failed > 0) return 'bg-warning';
  return 'progress-bar-striped progress-bar-animated';
}
